/**
 * DataPad.jsx  — iPad/Tablet optimised production entry screen
 * 
 * Replaces "Follow Up" tab from ProductionPlanning.
 * Accessed from MONITORING module.
 *
 * Data flow:
 *   Entry → production_logs (DB) → 
 *     Style Registry (passport qty),
 *     Production Planning (actual vs planned),
 *     MIS (efficiency calculations),
 *     Inventory (cutting reduces fabric)
 *     All Dashboards (live KPIs)
 *
 * Column structure:
 *   # | Emp ID | Name | Desig | Process | SMV | Target | P1..Pn | Total | Lost | Eff%
 *
 * Features:
 *   - Pin login (4-digit, per-user sessions)
 *   - Cascading dropdowns: Factory → Section → Style → PO → Line
 *   - Allocation management (reassign workers to processes)
 *   - Time-locked columns (can edit current−1 or current hour only)
 *   - Big-button calculator keypad overlay
 *   - Period OR Hour mode toggle
 *   - Live push to all modules via API
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

const API = 'https://nirvikalp.integrashion.com/api';

// ── Universal line code — same formula everywhere ────────────────────────────
const getLineCode = (line, factory, idx) =>
  line?.line_code || line?.lineCode || line?.barcode ||
  `LINE${String(factory?.id||'0').slice(-3).padStart(3,'0')}-${String(line?.id||(idx||0)+1).padStart(2,'0')}`;

const saveLog = (data) =>
  fetch(`${API}/save_universal.php`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ category: 'planning', data }),  // same as FollowUp — confirmed working
  }).then(r => r.json()).catch(() => ({ success: false }));

const loadData = () =>
  fetch(`${API}/get_all_data.php`).then(r => r.json()).catch(() => null);

// ─── THEME (iPad-optimised — large tap targets, high contrast) ────────────────
const TP = {
  bg:      '#f8fafc',
  surf:    '#ffffff',
  panel:   '#f1f5f9',
  bdr:     '#e2e8f0',
  acc:     '#1d4ed8',
  accLt:   '#eff6ff',
  grn:     '#16a34a',
  grnLt:   '#dcfce7',
  warn:    '#d97706',
  warnLt:  '#fef3c7',
  red:     '#dc2626',
  redLt:   '#fee2e2',
  tx:      '#0f172a',
  tx2:     '#334155',
  mu:      '#64748b',
  // Touch sizes
  btnH:    52,
  inputH:  48,
  fs:      17,
  fsSm:    15,
  fsXs:    13,
};

// CSS injected once
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
*, *::before, *::after { box-sizing: border-box; margin:0; padding:0 }
html, body { font-family:'Inter',sans-serif; background:${TP.bg}; color:${TP.tx}; -webkit-font-smoothing:antialiased }
.dp { height:100vh; display:flex; flex-direction:column; overflow:hidden; font-size:${TP.fs}px; background:${TP.bg} }
/* Login */
.dp-login { flex:1; display:flex; align-items:center; justify-content:center; background:linear-gradient(135deg,#1e3a8a,#1d4ed8) }
.dp-login-box { background:#fff; border-radius:20px; padding:40px; width:380px; box-shadow:0 20px 60px rgba(0,0,0,.25) }
.dp-login-logo { font-size:28px; font-weight:800; color:${TP.acc}; text-align:center; margin-bottom:6px; letter-spacing:-1px }
.dp-login-sub { font-size:14px; color:${TP.mu}; text-align:center; margin-bottom:32px }
.dp-pin-dots { display:flex; justify-content:center; gap:12px; margin-bottom:20px }
.dp-pin-dot { width:18px; height:18px; border-radius:50%; border:2px solid #cbd5e1; transition:all .15s }
.dp-pin-dot.filled { background:${TP.acc}; border-color:${TP.acc} }
.dp-numpad { display:grid; grid-template-columns:repeat(3,1fr); gap:10px }
.dp-numpad button { height:64px; border:none; border-radius:12px; background:${TP.panel}; font-size:22px; font-weight:700; color:${TP.tx}; cursor:pointer; transition:all .1s; touch-action:manipulation }
.dp-numpad button:active, .dp-numpad button:hover { background:#dbeafe; color:${TP.acc} }
.dp-numpad button.del { background:#fee2e2; color:${TP.red} }
.dp-numpad button.ok  { background:${TP.acc}; color:#fff; grid-column:span 3 }
/* Header */
.dp-header { height:56px; background:${TP.acc}; display:flex; align-items:center; padding:0 20px; gap:14px; flex-shrink:0 }
.dp-header-title { font-size:18px; font-weight:800; color:#fff; letter-spacing:1px }
.dp-header-sep { width:1px; height:18px; background:rgba(255,255,255,.3) }
.dp-header-ctx { font-size:14px; color:rgba(255,255,255,.8) }
.dp-header-sp { flex:1 }
.dp-logout { padding:8px 16px; background:rgba(255,255,255,.2); border:1px solid rgba(255,255,255,.3); border-radius:8px; color:#fff; font-size:13px; font-weight:600; cursor:pointer }
/* Selection bar */
.dp-selbar { background:#fff; border-bottom:2px solid ${TP.bdr}; padding:12px 20px; display:flex; gap:12px; align-items:flex-end; flex-wrap:wrap; flex-shrink:0 }
.dp-fg { display:flex; flex-direction:column; gap:4px }
.dp-lbl { font-size:12px; font-weight:600; color:${TP.mu}; letter-spacing:.3px; text-transform:uppercase }
.dp-sel { height:${TP.inputH}px; background:${TP.panel}; border:2px solid ${TP.bdr}; border-radius:10px; color:${TP.tx}; font-size:${TP.fsSm}px; font-weight:500; padding:0 14px; outline:none; min-width:160px; transition:border-color .15s }
.dp-sel:focus { border-color:${TP.acc}; background:#eff6ff }
/* Mode toggle */
.dp-mode-toggle { display:flex; background:${TP.panel}; border-radius:10px; padding:3px; gap:2px }
.dp-mode-btn { padding:8px 18px; border:none; border-radius:8px; font-size:13px; font-weight:600; cursor:pointer; transition:all .15s; color:${TP.mu}; background:none }
.dp-mode-btn.on { background:#fff; color:${TP.acc}; box-shadow:0 1px 4px rgba(0,0,0,.1) }
/* Main content */
.dp-main { flex:1; overflow:hidden; display:flex; flex-direction:column }
/* Grid */
.dp-grid-wrap { flex:1; overflow:auto }
.dp-grid { border-collapse:separate; border-spacing:0; font-size:${TP.fsXs}px; width:max-content; min-width:100% }
.dp-grid th { background:${TP.panel}; padding:10px 12px; text-align:center; font-size:11px; font-weight:700; color:${TP.mu}; letter-spacing:.4px; text-transform:uppercase; border-bottom:2px solid ${TP.bdr}; position:sticky; top:0; z-index:3; white-space:nowrap }
.dp-grid th.sticky-l { position:sticky; left:0; z-index:4 }
.dp-grid th.col-period { background:#dbeafe; color:#1e40af; min-width:80px }
.dp-grid th.col-period.active { background:#1d4ed8; color:#fff }
.dp-grid th.col-period.locked { background:#f1f5f9; color:#94a3b8 }
.dp-grid td { padding:6px 10px; border-bottom:1px solid ${TP.bdr}; text-align:center; vertical-align:middle; white-space:nowrap }
.dp-grid td.sticky-l { background:#fff; position:sticky; left:0; z-index:2; text-align:left; border-right:2px solid ${TP.bdr} }
.dp-grid tr:hover td { background:#f8faff }
/* Allocation shift badge */
.alloc-shift { background:#fef3c7; color:#92400e; border:1px solid #fcd34d; font-size:10px; padding:1px 5px; border-radius:4px; margin-left:4px }
/* Entry cell */
.dp-cell-btn { width:72px; height:38px; border-radius:8px; border:2px solid ${TP.bdr}; background:${TP.surf}; font-size:15px; font-weight:700; color:${TP.tx}; cursor:pointer; transition:all .12s; touch-action:manipulation }
.dp-cell-btn:hover, .dp-cell-btn.active { border-color:${TP.acc}; background:${TP.accLt}; color:${TP.acc} }
.dp-cell-btn.filled { border-color:${TP.grn}; background:${TP.grnLt}; color:${TP.grn} }
.dp-cell-btn.locked { background:${TP.panel}; color:${TP.mu}; cursor:default; border-style:dashed }
/* Efficiency cell */
.eff-cell { font-weight:700; font-size:13px; padding:4px 8px; border-radius:20px }
.eff-h { background:${TP.grnLt};  color:${TP.grn}  }
.eff-m { background:${TP.warnLt}; color:${TP.warn} }
.eff-l { background:${TP.redLt};  color:${TP.red}  }
/* Keypad overlay */
.dp-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:100; display:flex; align-items:flex-end; justify-content:center }
.dp-keypad { background:#fff; border-radius:24px 24px 0 0; padding:20px 24px 32px; width:100%; max-width:420px; box-shadow:0 -8px 40px rgba(0,0,0,.2) }
.dp-kp-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:14px }
.dp-kp-title { font-size:13px; font-weight:700; color:${TP.mu}; text-transform:uppercase; letter-spacing:.5px }
.dp-kp-display { font-family:monospace; font-size:36px; font-weight:800; color:${TP.acc}; background:${TP.panel}; border-radius:12px; padding:12px 20px; text-align:right; margin-bottom:16px; min-height:68px; display:flex; align-items:center; justify-content:flex-end }
.dp-kp-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:10px }
.dp-kp-btn { height:72px; border:none; border-radius:14px; font-size:26px; font-weight:700; cursor:pointer; transition:all .1s; touch-action:manipulation }
.dp-kp-num { background:${TP.panel}; color:${TP.tx} }
.dp-kp-num:hover { background:#dbeafe; color:${TP.acc} }
.dp-kp-zero { grid-column:span 1 }
.dp-kp-del { background:#fee2e2; color:${TP.red} }
.dp-kp-ok { background:${TP.grn}; color:#fff; grid-column:span 3; height:60px; font-size:20px }
.dp-kp-ok:hover { background:#15803d }
.dp-kp-clear { background:#f1f5f9; color:${TP.mu} }
/* Allocation modal */
.dp-modal { position:fixed; inset:0; background:rgba(0,0,0,.5); z-index:200; display:flex; align-items:center; justify-content:center; padding:20px }
.dp-modal-box { background:#fff; border-radius:16px; padding:24px; width:100%; max-width:500px; box-shadow:0 20px 60px rgba(0,0,0,.3); max-height:90vh; overflow-y:auto }
/* Notification */
.dp-notif { position:fixed; top:70px; right:20px; z-index:300; background:#fff; border:1px solid ${TP.bdr}; border-radius:12px; padding:12px 18px; box-shadow:0 4px 20px rgba(0,0,0,.15); font-size:14px; font-weight:600; display:flex; align-items:center; gap:10px; max-width:360px; animation:slideIn .3s ease }
@keyframes slideIn { from { transform:translateX(120%); opacity:0 } to { transform:translateX(0); opacity:1 } }
.dp-notif.ok   { border-left:4px solid ${TP.grn}; color:${TP.grn} }
.dp-notif.warn { border-left:4px solid ${TP.warn}; color:${TP.warn} }
.dp-notif.err  { border-left:4px solid ${TP.red};  color:${TP.red} }
/* Status bar */
.dp-statusbar { height:40px; background:${TP.panel}; border-top:1px solid ${TP.bdr}; display:flex; align-items:center; padding:0 16px; gap:20px; font-size:12px; color:${TP.mu}; flex-shrink:0 }
.dp-stat { display:flex; align-items:center; gap:5px }
.dp-stat strong { color:${TP.tx}; font-weight:700 }
`;

// ─── PIN LOGIN ────────────────────────────────────────────────────────────────
// Simple static PIN list — extend to user table when ready
const USERS = [
  { pin: '1234', name: 'Supervisor',  role: 'supervisor' },
  { pin: '0000', name: 'Floor Lead',  role: 'floor'      },
  { pin: '9999', name: 'Admin',       role: 'admin'      },
];

function LoginScreen({ onLogin }) {
  const [pin, setPin] = useState('');
  const [err, setErr] = useState('');

  const press = (v) => {
    if (v === 'DEL') { setPin(p => p.slice(0,-1)); setErr(''); return; }
    if (v === 'OK')  {
      const user = USERS.find(u => u.pin === pin);
      if (user) onLogin(user);
      else { setErr('Invalid PIN'); setPin(''); }
      return;
    }
    if (pin.length < 4) setPin(p => p + v);
  };

  useEffect(() => {
    if (pin.length === 4) {
      const user = USERS.find(u => u.pin === pin);
      if (user) { onLogin(user); return; }
      setErr('Invalid PIN'); setPin('');
    }
  }, [pin]);

  const KEYS = ['1','2','3','4','5','6','7','8','9','DEL','0','OK'];

  return (
    <div className="dp-login">
      <div className="dp-login-box">
        <div className="dp-login-logo">DataPad</div>
        <div className="dp-login-sub">Production Entry · Touch Interface</div>
        <div className="dp-pin-dots">
          {[0,1,2,3].map(i => <div key={i} className={`dp-pin-dot ${i < pin.length ? 'filled' : ''}`}/>)}
        </div>
        {err && <div style={{color:'#dc2626',textAlign:'center',fontSize:14,marginBottom:12,fontWeight:600}}>{err}</div>}
        <div className="dp-numpad">
          {KEYS.map(k => (
            <button key={k} className={k==='DEL'?'del':k==='OK'?'ok':''} onClick={() => press(k)}>
              {k==='DEL' ? '⌫' : k==='OK' ? 'ENTER' : k}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── CALCULATOR KEYPAD ────────────────────────────────────────────────────────
function Keypad({ context, onConfirm, onClose }) {
  const [val, setVal] = useState('');
  const KEYS = ['7','8','9','4','5','6','1','2','3','CLR','0','⌫'];

  const press = (k) => {
    if (k === '⌫')  { setVal(v => v.slice(0,-1)); return; }
    if (k === 'CLR') { setVal(''); return; }
    if (val.length < 5) setVal(v => v + k);
  };

  return (
    <div className="dp-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="dp-keypad">
        <div className="dp-kp-header">
          <div className="dp-kp-title">
            {context.worker} · {context.period}
          </div>
          <button onClick={onClose} style={{background:'none',border:'none',fontSize:20,cursor:'pointer',color:'#94a3b8'}}>✕</button>
        </div>
        <div className="dp-kp-display">{val || '0'}</div>
        <div className="dp-kp-grid">
          {KEYS.map(k => (
            <button key={k}
              className={`dp-kp-btn ${k==='⌫'||k==='CLR'?'dp-kp-del':' dp-kp-num'}`}
              onClick={() => press(k)}>
              {k}
            </button>
          ))}
          <button className="dp-kp-btn dp-kp-ok" onClick={() => onConfirm(parseInt(val)||0)}>
            ✓ CONFIRM  {val ? `(${val} pcs)` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── ALLOCATION MODAL ─────────────────────────────────────────────────────────
function AllocationModal({ worker, processes, onSave, onClose }) {
  const [sel, setSel] = useState(worker.process || '');
  const [note, setNote] = useState('');

  return (
    <div className="dp-modal" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="dp-modal-box">
        <h3 style={{marginBottom:16,fontSize:17,fontWeight:800}}>Change Allocation</h3>
        <div style={{background:'#f8fafc',borderRadius:10,padding:14,marginBottom:16}}>
          <div style={{fontSize:13,color:'#64748b'}}>Worker</div>
          <div style={{fontWeight:700,fontSize:16}}>{worker.name} <span style={{fontWeight:400,color:'#64748b',fontSize:14}}>({worker.operatorId})</span></div>
          <div style={{fontSize:13,color:'#64748b',marginTop:4}}>Current: <strong style={{color:'#1d4ed8'}}>{worker.process || '—'}</strong></div>
        </div>
        <div style={{marginBottom:16}}>
          <label style={{fontSize:13,fontWeight:600,color:'#334155',display:'block',marginBottom:6}}>New Process</label>
          <select className="dp-sel" style={{width:'100%'}} value={sel} onChange={e=>setSel(e.target.value)}>
            <option value="">— Same as original —</option>
            {processes.map(p=><option key={p.procName||p} value={p.procName||p}>{p.procName||p}</option>)}
          </select>
        </div>
        <div style={{marginBottom:20}}>
          <label style={{fontSize:13,fontWeight:600,color:'#334155',display:'block',marginBottom:6}}>Note</label>
          <input className="dp-sel" style={{width:'100%'}} placeholder="Reason for shift (optional)" value={note} onChange={e=>setNote(e.target.value)}/>
        </div>
        <div style={{display:'flex',gap:10}}>
          <button onClick={onClose} style={{flex:1,height:52,borderRadius:10,border:'2px solid #e2e8f0',background:'#f8fafc',fontSize:15,fontWeight:700,cursor:'pointer'}}>Cancel</button>
          <button onClick={()=>onSave(sel,note)} style={{flex:1,height:52,borderRadius:10,border:'none',background:'#1d4ed8',color:'#fff',fontSize:15,fontWeight:700,cursor:'pointer'}}>✓ Save Allocation</button>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN DataPad COMPONENT ───────────────────────────────────────────────────
export default function DataPad({ db, setDb }) {
  const [user, setUser]     = useState(null);
  const [sel, setSel]       = useState({ factory:'', section:'', style:'', po:'', line:'', color:'', size:'' });
  const [mode, setMode]     = useState('PERIOD'); // 'PERIOD' | 'HOUR'
  const [workers, setWorkers] = useState([]); // [{id, operatorId, name, designation, process, smv, target, originalProcess, allocationShifted}]
  const [entries, setEntries] = useState({}); // {`workerId:colKey`: qty}
  const [dpView,   setDpView]   = useState('entry');  // 'entry' | 'log'
  const [logDate,  setLogDate]  = useState(() => new Date().toISOString().split('T')[0]);
  const [allocOverrides, setAllocOverrides] = useState({}); // {workerId: {process, note}}
  const [keypadCtx, setKpCtx] = useState(null);
  const [allocModal, setAllocModal] = useState(null);
  const [notif, setNotif]   = useState(null);
  const [saving, setSaving] = useState(false);

  const notify = (msg, type='ok') => {
    setNotif({ msg, type });
    setTimeout(() => setNotif(null), 3500);
  };

  // ── Derive columns (periods or hours) ─────────────────────────────────────
  const now = new Date();
  const currentHour = now.getHours();
  const today = now.toISOString().slice(0, 10);

  const atConfig = useMemo(() => {
    if (!sel.line) return null;
    // Try pm_available_time from db or localStorage
    const stored = (() => { try { return JSON.parse(localStorage.getItem('pm_availableTimes')) || []; } catch { return []; } })();
    return stored.find(a => a.date === today && (a.lineId === sel.line || a.line_id === sel.line))
      || stored.find(a => a.date === today)
      || { shiftHours: 10, periodDuration: 2, lunchMin: 30, teaMin: 10 };
  }, [sel.line, today]);

  const columns = useMemo(() => {
    if (!atConfig) return [];
    if (mode === 'PERIOD') {
      const netMin = (atConfig.shiftHours || 10) * 60 - (atConfig.lunchMin || 30) - (atConfig.teaMin || 10);
      const dur    = (atConfig.periodDuration || 2) * 60;
      const n      = Math.floor(netMin / dur);
      const shiftStart = 8; // assume 8am start
      return Array.from({ length: n }, (_, i) => {
        const startH = shiftStart + i * (atConfig.periodDuration || 2);
        return {
          key: `P${i+1}`,
          label: `P${i+1}`,
          sub: `${String(startH).padStart(2,'0')}:00`,
          startHour: startH,
          endHour: startH + (atConfig.periodDuration || 2),
          isActive: currentHour >= startH && currentHour < startH + (atConfig.periodDuration || 2) + 1,
          isLocked: currentHour < startH,
        };
      });
    } else {
      // Hour-by-hour columns
      const shiftStart = 8, shiftEnd = (atConfig.shiftHours || 10) + 8;
      return Array.from({ length: shiftEnd - shiftStart }, (_, i) => {
        const h = shiftStart + i;
        return {
          key: `H${h}`,
          label: `${String(h).padStart(2,'0')}:00`,
          sub: `${String(h+1).padStart(2,'0')}:00`,
          startHour: h,
          endHour: h + 1,
          isActive: currentHour === h || currentHour === h + 1,
          isLocked: currentHour < h,
        };
      });
    }
  }, [mode, atConfig, currentHour]);

  // ── Derive factories / sections ───────────────────────────────────────────
  const factories = useMemo(() =>
    (db?.suppliers || []).filter(s => s.supplierType === 'Garment Supplier'),
    [db?.suppliers]
  );

  const sections = ['Cutting', 'Sewing', 'Finishing', 'Packing'];

  const styles = useMemo(() => db?.mgmtStyles || [], [db?.mgmtStyles]);

  const styleObj = useMemo(() =>
    styles.find(s => s.styleRef === sel.style || s.id === sel.style),
    [styles, sel.style]
  );

  const pos = useMemo(() => {
    if (!styleObj) return [];
    const raw = styleObj.pos || styleObj.purchaseOrders || [];
    return raw.map(p => ({
      num:  p.num || p.po_no || p.poNo || '-',
      qty:  parseFloat(p.qty || p.quantity || 0) || 0,
      col:  p.col || p.color || '',
      date: p.date || p.ex_fty_date || '',
    })).filter(p => p.num !== '-');
  }, [styleObj]);

  const factory = useMemo(() =>
    factories.find(f => f.id === sel.factory || f.name === sel.factory),
    [factories, sel.factory]
  );

  const lines = useMemo(() => {
    if (!factory) return [];
    const allLines = factory.sewingLines || factory.lines || factory.productionLines || [];
    // Filter to only show lines allocated to the selected style
    if (sel.style) {
      const styleObj = (db?.mgmtStyles||[]).find(s=>s.styleRef===sel.style);
      const allocLines = styleObj?.allocatedLines || styleObj?.planningData?.allocatedLines || [];
      if (allocLines.length > 0) {
        const allocated = allLines.filter(l =>
          allocLines.some(al=>al.resId===l.id||al.name===(l.category||l.name))
        );
        if (allocated.length > 0) return allocated;
      }
    }
    return allLines;
  }, [factory, sel.style, db]);

  // Operations from workstation
  const ops = useMemo(() =>
    styleObj?.workstationData?.aggregatedOps || [],
    [styleObj]
  );

  // ── Load workers when line is selected ────────────────────────────────────
  useEffect(() => {
    if (!sel.line || !sel.factory) return;
    const stored = (() => { try { return JSON.parse(localStorage.getItem('pm_workers')) || []; } catch { return []; } })();
    const lineId = sel.line;
    const factId = factory?.id;
    const lineWorkers = stored.filter(w =>
      (w.line_id == lineId || w.lineId == lineId) &&
      (w.factory_id == factId || w.factoryId == factId)
    );
    if (lineWorkers.length > 0) {
      // Assign operations from OB in sequence
      setWorkers(lineWorkers.map((w, i) => ({
        id: w.id || w.operator_id,
        operatorId: w.operator_id || w.operatorId,
        name: w.name,
        designation: w.designation || 'Operator',
        process: ops[i % ops.length]?.procName || '—',
        smv: parseFloat(ops[i % ops.length]?.smv) || 0,
        target: ops[i % ops.length]?.smv > 0 ? Math.floor((60 / parseFloat(ops[i % ops.length].smv)) * 0.65) : 0,
        originalProcess: ops[i % ops.length]?.procName || '—',
        allocationShifted: false,
      })));
    } else {
      // Fallback: generic row
      const line = lines.find(l => String(l.id) === String(sel.line));
      const count = parseInt(line?.machines || line?.workers || 10);
      setWorkers(Array.from({ length: count }, (_, i) => ({
        id: `W${i+1}`, operatorId: `OP${String(i+1).padStart(3,'0')}`,
        name: `Worker ${i+1}`, designation: 'Operator',
        process: ops[i % Math.max(ops.length,1)]?.procName || '—',
        smv: parseFloat(ops[i % Math.max(ops.length,1)]?.smv) || 0,
        target: 0, originalProcess: ops[i % Math.max(ops.length,1)]?.procName || '—',
        allocationShifted: false,
      })));
    }
    setEntries({});
    setAllocOverrides({});
  }, [sel.line, sel.factory, sel.style]);

  // ── Cell interaction ──────────────────────────────────────────────────────
  const openKeypad = (workerId, colKey, worker, col) => {
    if (col.isLocked) { notify('This period is not active yet', 'warn'); return; }
    setKpCtx({ workerId, colKey, worker: worker.name, period: col.label });
  };

  const confirmEntry = useCallback(async (qty) => {
    // ── MANDATORY: color and size must be set ─────────────────────────────
    if (!sel.color || sel.color.trim() === '') {
      alert('⚠ COLOR is required. Select a PO that has a color before entering production data.');
      setKpCtx(null); return;
    }
    if (!sel.size || sel.size.trim() === '') {
      alert('⚠ SIZE is required. Select the size you are working on before entering production data.');
      setKpCtx(null); return;
    }
    const { workerId, colKey } = keypadCtx;
    const key = `${workerId}:${colKey}`;
    const newEntries = { ...entries, [key]: qty };
    setEntries(newEntries);
    setKpCtx(null);

    // Push to DB
    const worker = workers.find(w => w.id === workerId);
    const col = columns.find(c => c.key === colKey);
    // Compute the unique line code for this entry
    const _fac    = factories.find(f => f.id === sel.factory) || {};
    const _facLines = _fac.sewingLines || _fac.lines || _fac.productionLines || [];
    const _curLine  = _facLines.find(l => l.id === parseInt(sel.line)||l.category===sel.section||l.name===sel.section);
    const _lineIdx  = _curLine ? _facLines.indexOf(_curLine) : 0;
    const _lineCode = getLineCode(_curLine||{category:sel.section}, _fac, _lineIdx);

    const _sec = (sel.section||'').toLowerCase();
    const log = {
      id: Date.now(), factory: sel.factory, section: sel.section,
      line: sel.section,
      line_id: _lineCode,
      line_code: _lineCode,
      style_ref: sel.style, style: sel.style,
      po_no: sel.po,
      color: sel.color,
      size: sel.size,
      // Map qty to the right stage field so summary totals work
      cutting:   _sec==='cutting'   ? qty : 0,
      sewing:    _sec==='sewing'    ? qty : 0,
      finishing: _sec==='finishing' ? qty : 0,
      quantity:  qty,
      type: _sec==='cutting'?'CUT':_sec==='sewing'?'SEW':'PACK',
      operator_id: worker?.operatorId, worker_name: worker?.name,
      process: allocOverrides[workerId]?.process || worker?.process,
      smv: worker?.smv, qty, col_key: colKey, date: today,
      period_start: col?.startHour, period_end: col?.endHour,
      source: 'DATAPAD', created_at: new Date().toISOString(),
    };
    setSaving(true);
    const result = await saveLog(log);
    setSaving(false);
    if (result?.success) {
      notify(`Saved: ${qty} pcs for ${worker?.name || 'worker'} ✓`);
      // Refresh logs so log view shows immediately
      loadData().then(fresh => {
        if (fresh?.productionLogs) setDb(prev=>({...prev, productionLogs:fresh.productionLogs}));
      });
    } else {
      notify(`Saved locally — sync issue: ${result?.error||'check server'}`, 'warn');
    }
  }, [keypadCtx, entries, workers, columns, sel, allocOverrides, today]);

  // ── Allocation override ───────────────────────────────────────────────────
  const saveAllocation = (workerId, newProcess, note) => {
    const prev = workers.find(w => w.id === workerId);
    const isShift = newProcess && newProcess !== prev?.originalProcess;
    setAllocOverrides(p => ({ ...p, [workerId]: { process: newProcess, note } }));
    setWorkers(ws => ws.map(w => w.id === workerId
      ? { ...w, process: newProcess || w.originalProcess, allocationShifted: isShift }
      : w
    ));
    setAllocModal(null);
    if (isShift) notify(`⚠ Allocation shifted: ${prev?.name} → ${newProcess}`, 'warn');
    else notify(`Allocation saved for ${prev?.name}`);
  };

  // ── Summary calculations ──────────────────────────────────────────────────
  const getWorkerTotal = (workerId) =>
    columns.reduce((s, c) => s + (entries[`${workerId}:${c.key}`] || 0), 0);

  const getWorkerEff = (workerId) => {
    const w = workers.find(x => x.id === workerId);
    if (!w || !w.smv) return 0;
    const total = getWorkerTotal(workerId);
    const workedCols = columns.filter(c => !c.isLocked && entries[`${workerId}:${c.key}`] !== undefined).length || 1;
    const availMin = workedCols * (atConfig?.periodDuration || 2) * 60;
    return Math.round((total * w.smv) / availMin * 100);
  };

  const colTotal = (colKey) =>
    workers.reduce((s, w) => s + (entries[`${w.id}:${colKey}`] || 0), 0);

  const grandTotal = workers.reduce((s, w) => s + getWorkerTotal(w.id), 0);
  const lineSmv    = workers.reduce((s, w) => {
    const t = getWorkerTotal(w.id);
    return s + t * w.smv;
  }, 0);
  const workedCols = columns.filter(c => !c.isLocked).length || 1;
  const totalAvailMin = workers.length * workedCols * (atConfig?.periodDuration || 2) * 60;
  const lineEff = totalAvailMin ? Math.round((lineSmv / totalAvailMin) * 100) : 0;

  if (!user) return (
    <>
      <style>{CSS}</style>
      <div className="dp"><LoginScreen onLogin={setUser}/></div>
    </>
  );

  return (
    <>
      <style>{CSS}</style>
      <div className="dp">

        {/* HEADER */}
        <div className="dp-header">
          <div className="dp-header-title">DataPad</div>
          <div className="dp-header-sep"/>
          <div className="dp-header-ctx">
            {[sel.factory&&factory?.name, sel.section, sel.style, sel.po&&`PO: ${sel.po}`].filter(Boolean).join(' · ') || 'Select below to begin'}
          </div>
          <div className="dp-header-sp"/>
          {/* View toggle */}
          <div style={{display:'flex',gap:'6px',marginRight:'12px'}}>
            <button onClick={()=>setDpView('entry')}
              style={{padding:'4px 12px',borderRadius:'5px',border:'none',cursor:'pointer',fontSize:'12px',fontWeight:'bold',
                background:dpView==='entry'?'rgba(255,255,255,0.95)':'rgba(255,255,255,0.15)',
                color:dpView==='entry'?'#0073ea':'#fff'}}>
              ✏ Entry
            </button>
            <button onClick={()=>setDpView('log')}
              style={{padding:'4px 12px',borderRadius:'5px',border:'none',cursor:'pointer',fontSize:'12px',fontWeight:'bold',
                background:dpView==='log'?'rgba(255,255,255,0.95)':'rgba(255,255,255,0.15)',
                color:dpView==='log'?'#0073ea':'#fff'}}>
              📋 Log View
            </button>
          </div>
          <span style={{fontSize:13,color:'rgba(255,255,255,.7)',marginRight:8}}>
            👤 {user.name}
          </span>
          <button className="dp-logout" onClick={()=>setUser(null)}>Logout</button>
        </div>

        {/* SELECTION BAR */}
        <div className="dp-selbar">
          <div className="dp-fg">
            <span className="dp-lbl">Factory</span>
            <select className="dp-sel" value={sel.factory} onChange={e=>setSel({factory:e.target.value,section:'',style:'',po:'',line:''})}>
              <option value="">— Select —</option>
              {factories.map(f=><option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>
          <div className="dp-fg">
            <span className="dp-lbl">Section</span>
            <select className="dp-sel" value={sel.section} onChange={e=>setSel({...sel,section:e.target.value,po:'',line:''})} disabled={!sel.factory}>
              <option value="">— Select —</option>
              {sections.map(s=><option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="dp-fg">
            <span className="dp-lbl">Style</span>
            <select className="dp-sel" value={sel.style} onChange={e=>setSel({...sel,style:e.target.value,po:'',line:''})} disabled={!sel.section}>
              <option value="">— Select —</option>
              {styles.map(s=><option key={s.id} value={s.styleRef||s.id}>{s.styleRef||s.name}</option>)}
            </select>
          </div>
          <div className="dp-fg">
            <span className="dp-lbl">PO</span>
            <select className="dp-sel" value={sel.po} onChange={e=>{
                const poNum = e.target.value;
                // Find the PO to auto-populate color
                const style = (db?.mgmtStyles||[]).find(s=>s.styleRef===sel.style)||{};
                const poObj = (style.pos||[]).find(p=>p.num===poNum||p.po===poNum)||{};
                setSel({...sel, po:poNum, line:'', color:poObj.col||poObj.color||poObj.clr||'', size:''});
              }} disabled={!sel.style}>
              <option value="">— Select —</option>
              {pos.map(p=><option key={p.num} value={p.num}>{p.num}{p.col?` · ${p.col}`:''} ({(p.qty||0).toLocaleString()} pcs)</option>)}
            </select>
          </div>
          {/* COLOR — auto-populated from PO, shown read-only */}
          <div className="dp-fg">
            <span className="dp-lbl" style={{color: sel.color?'#0369a1':'#e2445c', fontWeight:'bold'}}>
              Color {!sel.color && '⚠ Required'}
            </span>
            <div className="dp-sel" style={{
              background: sel.color?'#e0f2fe':'#fff0f0',
              border: `1px solid ${sel.color?'#7dd3fc':'#e2445c'}`,
              borderRadius:'6px', padding:'7px 10px',
              color: sel.color?'#0369a1':'#9ca3af',
              fontSize:'0.9rem', minHeight:'36px', display:'flex', alignItems:'center'
            }}>
              {sel.color || 'Select a PO first →'}
            </div>
          </div>
          {/* SIZE — mandatory selector */}
          <div className="dp-fg">
            <span className="dp-lbl" style={{color: sel.size?'#166534':'#e2445c', fontWeight:'bold'}}>
              Size {!sel.size && '⚠ Required'}
            </span>
            <select className="dp-sel"
              value={sel.size}
              onChange={e=>setSel({...sel,size:e.target.value})}
              disabled={!sel.color}
              style={{borderColor: sel.size?'#c5c7d4':sel.color?'#e2445c':'#c5c7d4',
                      background: !sel.color?'#f5f5f5':'white'}}>
              <option value="">— Select Size * —</option>
              {(() => {
                const styleObj = (db?.mgmtStyles||[]).find(s=>s.styleRef===sel.style)||{};
                const poObj    = (styleObj.pos||[]).find(p=>p.num===sel.po||p.po===sel.po)||{};
                // Parse sizeBreakdown string "S:1000,M:500,L:1000" → ['S','M','L']
                const rawSB = poObj.sizes||poObj.sizeBreakdown||poObj.size_breakdown||'';
                let sizes = [];
                if (Array.isArray(rawSB)) {
                  sizes = rawSB.map(sz=>typeof sz==='string'?sz:(sz.size||sz.label||'')).filter(Boolean);
                } else if (typeof rawSB === 'string' && rawSB) {
                  sizes = rawSB.split(',').map(p=>p.trim().split(':')[0].trim()).filter(Boolean);
                }
                // Also try po_size_table
                if (sizes.length === 0) {
                  const poNum = (sel.po||'').split(/[\s·•]/)[0];
                  const stRef = (db?.mgmtStyles||[]).find(s=>s.styleRef===sel.style)?.styleRef||sel.style;
                  sizes = [...new Set((db?.poSizeTable||[])
                    .filter(r=>r.style_ref===stRef&&r.po_no===poNum&&r.color===sel.color)
                    .map(r=>r.size).filter(Boolean))];
                }
                if (sizes.length > 0) return sizes.map(sz=>{
                  const s = typeof sz==='string'?sz:(sz.size||sz.label||'');
                  return <option key={s} value={s}>{s}</option>;
                });
                // Fallback common sizes if PO has no breakdown yet
                return ['XS','S','M','L','XL','XXL','XXXL','28','30','32','34','36','38']
                  .map(s=><option key={s} value={s}>{s}</option>);
              })()}
            </select>
          </div>
          <div className="dp-fg">
            <span className="dp-lbl">Line / Table</span>
            <select className="dp-sel" value={sel.line} onChange={e=>setSel({...sel,line:e.target.value})} disabled={!sel.po}>
              <option value="">— Select —</option>
              {lines.map((l,li)=>{
              const fac = (db?.factories||db?.factoryList||[]).find(f=>f.name===sel.factory)||{};
              const facNum = String(fac.id||1).slice(-3).padStart(3,'0');
              const lineCode = l.lineCode||l.uid||('LINE'+facNum+'-'+String(li+1).padStart(2,'0'));
              const lineName = l.category||l.name||('Line '+(li+1));
              return <option key={l.id||li} value={l.id||li}>{lineName} · {lineCode}</option>;
            })}
            </select>
          </div>
          {/* Mode toggle */}
          <div className="dp-mode-toggle" style={{alignSelf:'flex-end',marginLeft:8}}>
            <button className={`dp-mode-btn ${mode==='PERIOD'?'on':''}`} onClick={()=>setMode('PERIOD')}>Periods</button>
            <button className={`dp-mode-btn ${mode==='HOUR'?'on':''}`} onClick={()=>setMode('HOUR')}>Hours</button>
          </div>
        </div>

        {/* NO LINE SELECTED */}
        {!sel.line && (
          <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:12,color:'#64748b'}}>
            <div style={{fontSize:48,opacity:.2}}>🏭</div>
            <div style={{fontSize:16}}>Select Factory → Section → Style → PO → Line to begin entry</div>
          </div>
        )}

        {/* MAIN GRID */}
        {sel.line && workers.length > 0 && (
          <div className="dp-main">
            <div className="dp-grid-wrap">
              <table className="dp-grid">
                <thead>
                  <tr>
                    {/* Fixed left cols */}
                    <th className="sticky-l" style={{minWidth:36}}>#</th>
                    <th style={{minWidth:90}}>Emp ID</th>
                    <th style={{minWidth:160}}>Name</th>
                    <th style={{minWidth:100}}>Designation</th>
                    <th style={{minWidth:160}}>Process</th>
                    <th style={{minWidth:70}}>SMV</th>
                    <th style={{minWidth:70}}>Target</th>
                    {/* Period/Hour cols */}
                    {columns.map(c => (
                      <th key={c.key}
                        className={`col-period ${c.isActive?'active':c.isLocked?'locked':''}`}>
                        {c.label}
                        <div style={{fontSize:10,fontWeight:400}}>{c.sub}</div>
                      </th>
                    ))}
                    <th style={{minWidth:70}}>Total</th>
                    <th style={{minWidth:70}}>Lost</th>
                    <th style={{minWidth:70}}>Eff%</th>
                  </tr>
                </thead>
                <tbody>
                  {workers.map((w, i) => {
                    const total = getWorkerTotal(w.id);
                    const effVal = getWorkerEff(w.id);
                    const effCls = effVal >= 90 ? 'eff-h' : effVal >= 70 ? 'eff-m' : effVal > 0 ? 'eff-l' : '';
                    const isShifted = w.allocationShifted || allocOverrides[w.id]?.process;
                    return (
                      <tr key={w.id}>
                        <td className="sticky-l" style={{fontWeight:700,color:'#94a3b8',width:36,textAlign:'center'}}>{i+1}</td>
                        <td style={{fontFamily:'monospace',fontSize:13,color:'#1d4ed8'}}>{w.operatorId}</td>
                        <td style={{fontWeight:600,textAlign:'left'}}>
                          {w.name}
                          {isShifted && <span className="alloc-shift">⚠ shifted</span>}
                        </td>
                        <td style={{color:'#64748b',fontSize:13}}>{w.designation}</td>
                        <td style={{textAlign:'left'}}>
                          <div style={{display:'flex',alignItems:'center',gap:8}}>
                            <span style={{
                              background: isShifted ? '#fef3c7' : '#dbeafe',
                              color: isShifted ? '#92400e' : '#1e40af',
                              padding:'2px 8px', borderRadius:6, fontSize:12, fontWeight:600
                            }}>{allocOverrides[w.id]?.process || w.process}</span>
                            <button
                              onClick={() => setAllocModal(w)}
                              style={{background:'none',border:'1px solid #e2e8f0',borderRadius:6,padding:'2px 7px',fontSize:11,cursor:'pointer',color:'#64748b'}}>
                              ✏
                            </button>
                          </div>
                        </td>
                        <td style={{fontFamily:'monospace',fontWeight:700,color:'#059669'}}>{w.smv?.toFixed(3)||'—'}</td>
                        <td style={{fontFamily:'monospace',color:'#1d4ed8'}}>{w.target||'—'}</td>
                        {columns.map(c => {
                          const ek = `${w.id}:${c.key}`;
                          const val = entries[ek];
                          return (
                            <td key={c.key} style={{padding:4}}>
                              <button
                                className={`dp-cell-btn ${c.isLocked?'locked':val!=null?'filled':''}`}
                                onClick={() => openKeypad(w.id, c.key, w, c)}>
                                {val ?? (c.isLocked ? '–' : '·')}
                              </button>
                            </td>
                          );
                        })}
                        <td style={{fontWeight:800,fontSize:15,color:'#0f172a'}}>{total||'—'}</td>
                        <td>
                          <button
                            onClick={() => {/* lost time entry */}}
                            style={{background:'#fff7ed',border:'1px solid #fed7aa',borderRadius:6,padding:'4px 8px',fontSize:12,cursor:'pointer',color:'#c2410c'}}>
                            +LT
                          </button>
                        </td>
                        <td>
                          {effVal > 0
                            ? <span className={`eff-cell ${effCls}`}>{effVal}%</span>
                            : <span style={{color:'#94a3b8',fontSize:12}}>—</span>}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Totals row */}
                  <tr style={{background:'#f0f9ff',fontWeight:800,borderTop:'2px solid #1d4ed8'}}>
                    <td className="sticky-l" colSpan={2} style={{fontWeight:700,color:'#1d4ed8',textAlign:'left',padding:'8px 12px'}}>LINE TOTAL</td>
                    <td colSpan={5} style={{textAlign:'left',padding:'8px 12px',color:'#64748b',fontSize:13}}>
                      {workers.length} workers · SMV {styleObj?.workstationData?.smv?.toFixed(2)||'—'} min
                    </td>
                    {columns.map(c=>(
                      <td key={c.key} style={{fontFamily:'monospace',fontWeight:800,color:'#1e40af',fontSize:15}}>
                        {colTotal(c.key)||'—'}
                      </td>
                    ))}
                    <td style={{fontFamily:'monospace',fontWeight:800,fontSize:16,color:'#1e40af'}}>{grandTotal||'—'}</td>
                    <td/>
                    <td>
                      {lineEff > 0
                        ? <span className={`eff-cell ${lineEff>=90?'eff-h':lineEff>=70?'eff-m':'eff-l'}`}>{lineEff}%</span>
                        : <span style={{color:'#94a3b8'}}>—</span>}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* STATUS BAR */}
            <div className="dp-statusbar">
              <div className="dp-stat">🕐 <strong>{String(currentHour).padStart(2,'0')}:00</strong></div>
              <div className="dp-stat">📦 Total: <strong>{grandTotal.toLocaleString()} pcs</strong></div>
              <div className="dp-stat">⚡ Line Eff: <strong style={{color:lineEff>=90?TP.grn:lineEff>=70?TP.warn:TP.red}}>{lineEff||0}%</strong></div>
              <div className="dp-stat">👥 Workers: <strong>{workers.length}</strong></div>
              {saving && <div className="dp-stat" style={{marginLeft:'auto',color:'#1d4ed8'}}>⟳ Saving…</div>}
              {!saving && grandTotal > 0 && <div className="dp-stat" style={{marginLeft:'auto',color:'#16a34a'}}>✓ Data saved</div>}
            </div>
          </div>
        )}

        {/* KEYPAD OVERLAY */}
        {keypadCtx && (
          <Keypad context={keypadCtx} onConfirm={confirmEntry} onClose={()=>setKpCtx(null)}/>
        )}

        {/* ── HISTORICAL GRID VIEW — same grid as entry, populated from DB ──── */}
        {dpView === 'log' && (
          <div style={{position:'absolute',inset:0,background:'#fff',zIndex:200,display:'flex',flexDirection:'column',overflow:'hidden'}}>
            {/* Log header — same style as DataPad header */}
            <div style={{background:'#1e1f2e',padding:'0 20px',height:'56px',display:'flex',alignItems:'center',gap:'16px',flexShrink:0}}>
              <div style={{color:'#fff',fontWeight:'800',fontSize:'18px',letterSpacing:'1px'}}>DataPad</div>
              <div style={{width:'1px',height:'18px',background:'rgba(255,255,255,.3)'}}/>
              <div style={{color:'rgba(255,255,255,.8)',fontSize:'14px'}}>📋 Entry Log — Review Mode</div>
              <div style={{flex:1}}/>
              {/* Date picker */}
              <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                <button onClick={()=>{const d=new Date(logDate);d.setDate(d.getDate()-1);setLogDate(d.toISOString().split('T')[0]);}}
                  style={{padding:'4px 8px',background:'rgba(255,255,255,.15)',border:'none',borderRadius:'5px',color:'#fff',cursor:'pointer',fontSize:'14px'}}>‹</button>
                <input type="date" value={logDate} onChange={e=>setLogDate(e.target.value)}
                  style={{padding:'5px 10px',border:'1px solid rgba(255,255,255,.3)',borderRadius:'5px',
                    background:'rgba(255,255,255,.1)',color:'#fff',fontSize:'0.9rem',cursor:'pointer'}}/>
                <button onClick={()=>{const d=new Date(logDate);d.setDate(d.getDate()+1);setLogDate(d.toISOString().split('T')[0]);}}
                  style={{padding:'4px 8px',background:'rgba(255,255,255,.15)',border:'none',borderRadius:'5px',color:'#fff',cursor:'pointer',fontSize:'14px'}}>›</button>
                <button onClick={()=>setLogDate(new Date().toISOString().split('T')[0])}
                  style={{padding:'4px 10px',background:'#0073ea',border:'none',borderRadius:'5px',color:'#fff',fontSize:'12px',cursor:'pointer',fontWeight:'bold'}}>Today</button>
              </div>
              <button onClick={()=>setDpView('entry')}
                style={{padding:'5px 14px',background:'rgba(255,255,255,.15)',border:'none',borderRadius:'5px',
                  color:'#fff',fontSize:'0.82rem',cursor:'pointer'}}>← Live Entry</button>
            </div>

            {/* Grid body — same layout as live entry, filled from db.productionLogs */}
            {(() => {
              const histLogs = (db?.productionLogs || []).filter(l => {
                if (l.date !== logDate) return false;
                // If a style is selected, filter to it
                if (sel.style && l.style && l.style !== sel.style && l.style_ref !== sel.style) return false;
                if (sel.factory && l.factory && l.factory !== sel.factory) return false;
                return true;
              });

              // Build lookup: operatorId → periodKey → log entry
              const hist = {};
              histLogs.forEach(l => {
                const opId  = l.operator || l.operator_id || l.worker_name || 'Unknown';
                const period= l.hour || l.col_key || l.period_start || '';
                if (!hist[opId]) hist[opId] = {};
                if (!hist[opId][period]) hist[opId][period] = { cut:0, sew:0, fin:0, entries:[] };
                const _lsec = (l.section||l.source_section||'').toLowerCase();
                // Support both FollowUp (has cutting/sewing/finishing) and DataPad (may have only qty+type)
                const _lcut = parseFloat(l.cutting||0) || (l.type==='CUT'||_lsec==='cutting'?parseFloat(l.qty||0):0);
                const _lsew = parseFloat(l.sewing||0)  || (l.type==='SEW'||_lsec==='sewing' ?parseFloat(l.qty||0):0);
                const _lfin = parseFloat(l.finishing||0)||(l.type==='PACK'||_lsec==='finishing'?parseFloat(l.qty||0):0);
                hist[opId][period].cut     += _lcut;
                hist[opId][period].sew     += _lsew;
                hist[opId][period].fin     += _lfin;
                hist[opId][period].entries.push(l);
              });

              // Collect all unique operators and periods from hist data
              const histOps     = Object.keys(hist);
              const histPeriods = [...new Set(histLogs.map(l=>l.hour||l.col_key||l.period_start||'').filter(Boolean))].sort();

              // Summary totals
              const _getQty = (l,stage) => {
                const sec=(l.section||'').toLowerCase();
                if(stage==='cut') return parseFloat(l.cutting||0)||(l.type==='CUT'||sec==='cutting'?parseFloat(l.qty||0):0);
                if(stage==='sew') return parseFloat(l.sewing||0) ||(l.type==='SEW'||sec==='sewing' ?parseFloat(l.qty||0):0);
                return parseFloat(l.finishing||0)||(l.type==='PACK'||sec==='finishing'?parseFloat(l.qty||0):0);
              };
              const totCut = histLogs.reduce((s,l)=>s+_getQty(l,'cut'),0);
              const totSew = histLogs.reduce((s,l)=>s+_getQty(l,'sew'),0);
              const totFin = histLogs.reduce((s,l)=>s+_getQty(l,'fin'),0);

              return (
                <div style={{display:'flex',flexDirection:'column',flex:1,overflow:'auto',background:'#f5f6f8'}}>

                  {/* ── SECTION 1: CUMULATIVE TOTALS ──────────────────────────── */}
                  <div style={{background:'white',borderBottom:'1px solid #e6e9ef',padding:'12px 20px'}}>
                    <div style={{fontSize:'0.68rem',fontWeight:'700',color:'#676879',textTransform:'uppercase',letterSpacing:'1px',marginBottom:'10px'}}>
                      Cumulative Total — All Time ({sel.style||'All Styles'})
                    </div>
                    {(() => {
                      const allLogs = (db?.productionLogs||[]).filter(l=>
                        (!sel.style||l.style===sel.style||l.style_ref===sel.style)&&
                        (!sel.factory||!l.factory||l.factory===sel.factory)
                      );
                      const fuLogs = allLogs.filter(l=>(l.source||'').toUpperCase()==='FOLLOWUP');
                      const dpLogs = allLogs.filter(l=>(l.source||'').toUpperCase()==='DATAPAD');
                      const sum = (arr,f)=>arr.reduce((s,l)=>s+parseFloat(l[f]||0),0);
                      const cols = [
                        {label:'TOTAL ENTRIES',val:allLogs.length,c:'#676879',sub:''},
                        {label:'CUT',val:Math.round(sum(allLogs,'cutting')),c:'#e2445c',sub:'pieces'},
                        {label:'SEWING',val:Math.round(sum(allLogs,'sewing')),c:'#0073ea',sub:'pieces'},
                        {label:'FINISHING',val:Math.round(sum(allLogs,'finishing')),c:'#00a65e',sub:'pieces'},
                      ];
                      return (
                        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px',marginBottom:'8px'}}>
                          {cols.map(c=>(
                            <div key={c.label} style={{background:'#f5f6f8',borderRadius:'8px',padding:'10px 12px'}}>
                              <div style={{fontSize:'0.6rem',color:'#9699a6',textTransform:'uppercase',letterSpacing:'0.5px'}}>{c.label}</div>
                              <div style={{fontSize:'1.3rem',fontWeight:'900',color:c.c,lineHeight:1.2}}>{c.val.toLocaleString()}</div>
                              {c.sub&&<div style={{fontSize:'0.62rem',color:'#9699a6'}}>{c.sub}</div>}
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px'}}>
                      {[['FOLLOWUP','#7c3aed'],['DATAPAD','#0073ea']].map(([src,clr])=>{
                        const srcLogs=(db?.productionLogs||[]).filter(l=>
                          (l.source||'').toUpperCase()===src&&
                          (!sel.style||l.style===sel.style)&&
                          (!sel.factory||!l.factory||l.factory===sel.factory)
                        );
                        const cut=Math.round(srcLogs.reduce((s,l)=>s+parseFloat(l.cutting||0),0));
                        const sew=Math.round(srcLogs.reduce((s,l)=>s+parseFloat(l.sewing||0),0));
                        const fin=Math.round(srcLogs.reduce((s,l)=>s+parseFloat(l.finishing||0),0));
                        return (
                          <div key={src} style={{background:'#f5f6f8',borderRadius:'8px',padding:'8px 12px',borderLeft:`3px solid ${clr}`}}>
                            <div style={{fontSize:'0.65rem',fontWeight:'700',color:clr,marginBottom:'4px'}}>{src} ({srcLogs.length} entries)</div>
                            <div style={{display:'flex',gap:'16px',fontSize:'0.75rem'}}>
                              {cut>0&&<span><span style={{color:'#9699a6'}}>Cut </span><strong style={{color:'#e2445c'}}>{cut}</strong></span>}
                              {sew>0&&<span><span style={{color:'#9699a6'}}>Sew </span><strong style={{color:'#0073ea'}}>{sew}</strong></span>}
                              {fin>0&&<span><span style={{color:'#9699a6'}}>Fin </span><strong style={{color:'#00a65e'}}>{fin}</strong></span>}
                              {cut===0&&sew===0&&fin===0&&<span style={{color:'#9699a6',fontSize:'0.7rem'}}>No data yet</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* ── SECTION 2: TODAY'S STATUS ──────────────────────────────── */}
                  <div style={{background:'white',borderBottom:'1px solid #e6e9ef',padding:'12px 20px',marginTop:'8px'}}>
                    <div style={{fontSize:'0.68rem',fontWeight:'700',color:'#676879',textTransform:'uppercase',letterSpacing:'1px',marginBottom:'10px'}}>
                      Today — {logDate}
                    </div>
                    {(() => {
                      const todayLogs = (db?.productionLogs||[]).filter(l=>
                        l.date===logDate&&
                        (!sel.style||l.style===sel.style||l.style_ref===sel.style)&&
                        (!sel.factory||!l.factory||l.factory===sel.factory)
                      );
                      const todayCut=Math.round(todayLogs.reduce((s,l)=>s+parseFloat(l.cutting||0),0));
                      const todaySew=Math.round(todayLogs.reduce((s,l)=>s+parseFloat(l.sewing||0),0));
                      const todayFin=Math.round(todayLogs.reduce((s,l)=>s+parseFloat(l.finishing||0),0));
                      const stages=[
                        {label:'CUTTING',val:todayCut,c:'#e2445c',icon:'✂'},
                        {label:'SEWING',val:todaySew,c:'#0073ea',icon:'🧵'},
                        {label:'FINISHING',val:todayFin,c:'#00a65e',icon:'📦'},
                      ];
                      return (
                        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'8px'}}>
                          {stages.map(s=>(
                            <div key={s.label} style={{border:`2px solid ${s.val>0?s.c:'#e6e9ef'}`,borderRadius:'8px',padding:'10px 14px',textAlign:'center',background:s.val>0?'white':'#f9f9f9'}}>
                              <div style={{fontSize:'1.1rem',marginBottom:'2px'}}>{s.icon}</div>
                              <div style={{fontSize:'0.6rem',color:'#9699a6',textTransform:'uppercase',letterSpacing:'0.5px'}}>{s.label}</div>
                              <div style={{fontSize:'1.6rem',fontWeight:'900',color:s.val>0?s.c:'#c5c7d4',lineHeight:1.1}}>{s.val||'—'}</div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>

                  {/* ── SECTION 3: PERIOD-WISE DATAPAD ────────────────────────── */}
                  <div style={{background:'white',padding:'12px 20px',marginTop:'8px',flex:1}}>
                    <div style={{fontSize:'0.68rem',fontWeight:'700',color:'#676879',textTransform:'uppercase',letterSpacing:'1px',marginBottom:'10px'}}>
                      DataPad — Period-wise Operator Detail ({logDate})
                    </div>
                    {(() => {
                      const dpToday=(db?.productionLogs||[]).filter(l=>
                        l.date===logDate&&(l.source||'').toUpperCase()==='DATAPAD'&&
                        (!sel.style||l.style===sel.style)&&
                        (!sel.factory||!l.factory||l.factory===sel.factory)
                      );
                      if (dpToday.length===0) return (
                        <div style={{textAlign:'center',padding:'30px',color:'#9699a6',fontSize:'0.85rem'}}>
                          No DataPad entries for today yet.<br/>
                          <span style={{fontSize:'0.75rem'}}>Entries appear here after operators confirm quantities in Entry mode.</span>
                        </div>
                      );
                      // Group by operator → period
                      const byOp={};
                      const periods=new Set();
                      dpToday.forEach(l=>{
                        const op=l.worker_name||l.operator_id||'Unknown';
                        const per=l.hour||l.col_key||l.period_start||'—';
                        const qty=parseFloat(l.sewing||l.cutting||l.finishing||l.quantity||0);
                        const smv=parseFloat(l.smv||0)*qty;
                        periods.add(per);
                        if(!byOp[op])byOp[op]={process:l.process||'',total:0,smv:0,periods:{}};
                        if(!byOp[op].periods[per])byOp[op].periods[per]=0;
                        byOp[op].periods[per]+=qty;
                        byOp[op].total+=qty;
                        byOp[op].smv+=smv;
                      });
                      const sortedPeriods=[...periods].sort();
                      const ops=Object.keys(byOp);
                      const grandTotal=ops.reduce((s,op)=>s+byOp[op].total,0);
                      const grandSmv=ops.reduce((s,op)=>s+byOp[op].smv,0);
                      const eff=grandSmv>0&&grandTotal>0?Math.round((grandSmv/grandTotal)*100):0;
                      return (
                        <>
                          {/* Summary */}
                          <div style={{display:'flex',gap:'12px',marginBottom:'12px',flexWrap:'wrap'}}>
                            <div style={{background:'#eff6ff',borderRadius:'6px',padding:'6px 12px',fontSize:'0.78rem'}}>
                              <span style={{color:'#9699a6'}}>Total </span><strong style={{color:'#0073ea'}}>{Math.round(grandTotal)} pcs</strong>
                            </div>
                            <div style={{background:'#f0fdf4',borderRadius:'6px',padding:'6px 12px',fontSize:'0.78rem'}}>
                              <span style={{color:'#9699a6'}}>SMV earned </span><strong style={{color:'#15803d'}}>{grandSmv.toFixed(1)} min</strong>
                            </div>
                            <div style={{background:eff>=75?'#f0fdf4':eff>=60?'#fef9c3':'#fff1f2',borderRadius:'6px',padding:'6px 12px',fontSize:'0.78rem'}}>
                              <span style={{color:'#9699a6'}}>Efficiency </span>
                              <strong style={{color:eff>=75?'#15803d':eff>=60?'#ca8a04':'#e2445c'}}>{eff>0?eff+'%':'—'}</strong>
                            </div>
                          </div>
                          {/* Period table */}
                          <div style={{overflowX:'auto'}}>
                            <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.78rem'}}>
                              <thead>
                                <tr style={{background:'#f5f6f8'}}>
                                  <th style={{padding:'8px 10px',textAlign:'left',borderBottom:'2px solid #e6e9ef',color:'#676879',fontSize:'0.68rem',textTransform:'uppercase',whiteSpace:'nowrap'}}>Operator / Process</th>
                                  {sortedPeriods.map(p=>(
                                    <th key={p} style={{padding:'8px 10px',textAlign:'center',borderBottom:'2px solid #e6e9ef',color:'#676879',fontSize:'0.68rem',textTransform:'uppercase',whiteSpace:'nowrap'}}>{p}</th>
                                  ))}
                                  <th style={{padding:'8px 10px',textAlign:'center',borderBottom:'2px solid #e6e9ef',color:'#0073ea',fontSize:'0.68rem',textTransform:'uppercase'}}>TOTAL</th>
                                  <th style={{padding:'8px 10px',textAlign:'center',borderBottom:'2px solid #e6e9ef',color:'#15803d',fontSize:'0.68rem',textTransform:'uppercase'}}>EFF%</th>
                                </tr>
                              </thead>
                              <tbody>
                                {ops.map(op=>{
                                  const d=byOp[op];
                                  const opEff=d.smv>0&&d.total>0?Math.round((d.smv/d.total)*100):0;
                                  return (
                                    <tr key={op} style={{borderBottom:'1px solid #f0f1f4'}}>
                                      <td style={{padding:'8px 10px'}}>
                                        <div style={{fontWeight:'600',color:'#323338'}}>{op}</div>
                                        <div style={{fontSize:'0.68rem',color:'#9699a6'}}>{d.process}</div>
                                      </td>
                                      {sortedPeriods.map(p=>(
                                        <td key={p} style={{padding:'8px 10px',textAlign:'center',color:'#323338',fontWeight:d.periods[p]>0?'600':'400'}}>
                                          {d.periods[p]>0?d.periods[p]:'—'}
                                        </td>
                                      ))}
                                      <td style={{padding:'8px 10px',textAlign:'center',fontWeight:'700',color:'#0073ea'}}>{Math.round(d.total)||'—'}</td>
                                      <td style={{padding:'8px 10px',textAlign:'center',fontWeight:'700',color:opEff>=75?'#15803d':opEff>=60?'#ca8a04':'#9699a6'}}>{opEff>0?opEff+'%':'—'}</td>
                                    </tr>
                                  );
                                })}
                                <tr style={{background:'#f5f6f8',fontWeight:'700'}}>
                                  <td style={{padding:'8px 10px',color:'#323338',fontSize:'0.72rem',textTransform:'uppercase'}}>Day Total</td>
                                  {sortedPeriods.map(p=>{
                                    const pTotal=ops.reduce((s,op)=>s+(byOp[op].periods[p]||0),0);
                                    return <td key={p} style={{padding:'8px 10px',textAlign:'center',color:pTotal>0?'#0073ea':'#9699a6'}}>{pTotal>0?Math.round(pTotal):'—'}</td>;
                                  })}
                                  <td style={{padding:'8px 10px',textAlign:'center',color:'#0073ea'}}>{Math.round(grandTotal)}</td>
                                  <td style={{padding:'8px 10px',textAlign:'center',color:eff>=75?'#15803d':eff>=60?'#ca8a04':'#e2445c'}}>{eff>0?eff+'%':'—'}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </>
                      );
                    })()}
                  </div>

                </div>       );
            })()}
          </div>
        )}

        {/* ALLOCATION MODAL */}
        {allocModal && (
          <AllocationModal
            worker={allocModal}
            processes={ops}
            onSave={(p,n)=>saveAllocation(allocModal.id, p, n)}
            onClose={()=>setAllocModal(null)}
          />
        )}

        {/* NOTIFICATION */}
        {notif && (
          <div className={`dp-notif ${notif.type}`}>
            {notif.type==='ok'?'✓':notif.type==='warn'?'⚠':'✕'} {notif.msg}
          </div>
        )}
      </div>
    </>
  );
}
